import { useCallback } from "react";
import { announce } from "@react-aria/live-announcer";
import { useQueryClient } from "@tanstack/react-query";
import { lowerFirst } from "lodash";
import { useTranslation } from "react-i18next";
import { useIsEditForm } from "@/widgets/classic-form/IsEditFormContext";
import {
    EVENT_DEFAULT_INSTANCE,
    type FormValues,
    RELATION_DEFAULT_INSTANCE,
    useFormAPI,
} from "@/widgets/classic-form/form";
import { useGetInitialFormValues } from "@/widgets/classic-form/form-control/useGetInitialFormValues";
import { getEventModelName } from "@/entities/data-model";
import {
    type EventFieldsGroup,
    type FormMetadataResponse,
    GroupType,
    type RelationshipFieldGroup,
    ResponseItemType,
    useMetadataQuery,
} from "@/entities/form-metadata";
import { getPersonOptions } from "@/entities/tree-person";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { logFCI } from "@/shared/lib/fci/logFCI";
import { buildResetFormValues } from "./buildResetFormValues";
import { getSuggestionLabel } from "./getSuggestionLabel";
import { transformPersonToFormValues } from "./transformPerson";

/**
 * Ensures field groups that should be visible have at least one default instance.
 * When quickfill data lacks values for a group (e.g., no Death event, no Father),
 * the group would otherwise end up with an empty instances array and disappear from the form.
 *
 * Mirrors the metadata converter logic: groups are preserved when they are
 * non-removable OR prefilled (`fieldsetDisplayStrategy !== "empty-dynamic"`).
 */
export const ensureNonRemovableDefaults = (values: FormValues, formMetadata: FormMetadataResponse): void => {
    for (const item of formMetadata.fieldGroupsAndFeatureContainers) {
        if (item.type !== ResponseItemType.FieldGroup) continue;

        if (item.groupType === GroupType.Event) {
            const eventGroup = item as EventFieldsGroup;
            // Skip groups with "empty-dynamic" strategy — they were never
            // shown by default, so quickfill shouldn't force them onto the form.
            if (eventGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;

            for (const fieldset of eventGroup.fieldsets) {
                const key = getEventModelName(fieldset);
                if (key && values.events[key as keyof FormValues["events"]]?.instances.length === 0) {
                    values.events[key as keyof FormValues["events"]]!.instances = [EVENT_DEFAULT_INSTANCE];
                }
            }
        } else if (item.groupType === GroupType.FamilyMember) {
            const familyGroup = item as RelationshipFieldGroup;
            if (familyGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;

            for (const fieldset of familyGroup.fieldsets) {
                const key = lowerFirst(fieldset.relation) as keyof FormValues["family"];
                if (values.family[key]?.instances.length === 0) {
                    values.family[key].instances = [RELATION_DEFAULT_INSTANCE];
                }
            }
        }
    }
};

export const useQuickFillSelection = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const formApi = useFormAPI();
    const { data: formMetadata } = useMetadataQuery();
    const isEditForm = useIsEditForm();
    const getInitialFormValues = useGetInitialFormValues({ isEditForm });

    const selectSuggestion = useCallback(
        async (suggestion: PersonSuggestionItem, treeId: string) => {
            const currentPersonId = formApi.getFieldValue("treePerson").personId;
            const label = getSuggestionLabel(suggestion);

            // Deselect: clicking the already-selected person
            if (currentPersonId === suggestion.PersonId) {
                announce(t("suggestions.suggestionUnselected", { label }), "polite");
                const initialValues = getInitialFormValues();
                const resetValues = buildResetFormValues(initialValues, formApi.getValues(), { preserveName: true });
                formApi.setValues(resetValues);
                return;
            }

            // Select or re-select
            try {
                const person = await queryClient.ensureQueryData(
                    getPersonOptions({ personId: suggestion.PersonId, treeId }),
                );
                const initialValues = getInitialFormValues();
                const base = buildResetFormValues(initialValues, formApi.getValues(), { preserveName: false });
                const transformed = transformPersonToFormValues(person, base, formMetadata);
                ensureNonRemovableDefaults(transformed, formMetadata);
                formApi.setValues(transformed);
                announce(t("suggestions.suggestionSelected", { label }), "polite");
            } catch (error) {
                announce(t("suggestions.suggestionSelectionFailed"), "assertive");
                logFCI({ errorType: "Convenience", errorInfo: `QuickFill selection failed: ${error}` });
            }
        },
        [formApi, formMetadata, getInitialFormValues, queryClient, t],
    );

    return { selectSuggestion };
};
